import { Dispatch, SetStateAction } from "react";
import { TruckDetail, TrucksListResponse } from "./trucks.schema";
import { NavigateFunction } from "react-router-dom";
import { handlePermissionError } from "@/utils/permission-check";
import { toaster } from "@/components/ui/toaster";
import {
  createTruck,
  CreateTruckProps,
  deleteTrucks,
  getTruck,
  getTrucksList,
  GetTrucksListProps,
  updateTruck,
  UpdateTruckProps,
} from "@/services/trucks.service";
import {
  FieldValues,
  UseFormGetValues,
  UseFormSetValue,
} from "react-hook-form";
import { getTrucksFilter, TLFRProps } from "./filter-obj";
import { getAxiosErrorMessage } from "@/utils/axios-error-message";
import { AppRoutes } from "@/constants/routes";
import { getTiposModulo, TipoModulo } from "@/services/aux-entities.service";

interface FetchTrucksHandlerProps {
  setLoadingTrucks: Dispatch<SetStateAction<boolean>>;
  setTrucks: Dispatch<SetStateAction<TrucksListResponse | null>>;
  navigate: NavigateFunction;
  fetchPermissions: () => Promise<void>;
  showLoad?: boolean;
  filters?: GetTrucksListProps;
}

export const fetchTrucksHandler = async ({
  fetchPermissions,
  navigate,
  setLoadingTrucks,
  setTrucks,
  showLoad = true,
  filters,
}: FetchTrucksHandlerProps) => {
  try {
    if (showLoad) setLoadingTrucks(true);

    const response = await getTrucksList({
      ...filters,
      "P.Pagina": filters?.["P.Pagina"] ?? 1,
      "P.Records": filters?.["P.Records"] ?? 10,
    });

    setTrucks(response);

    setLoadingTrucks(false);
  } catch (error) {
    const handled = await handlePermissionError(
      error,
      navigate,
      fetchPermissions,
    );
    if (!handled) {
      toaster.create({
        type: "error",
        title: "No se pudo cargar el listado de camiones.",
      });
    }
  } finally {
    setLoadingTrucks(false);
  }
};

interface ClearFiltersHandlerProps {
  setValue: UseFormSetValue<FieldValues>;
  setFiltersApplied: Dispatch<SetStateAction<boolean>>;
  submitFilters: (event: FieldValues) => Promise<void>;
  getValues: UseFormGetValues<FieldValues>;
  setOpenFilters: Dispatch<SetStateAction<boolean>>;
}

export const clearFiltersHandler = async ({
  setFiltersApplied,
  setValue,
  getValues,
  submitFilters,
  setOpenFilters,
}: ClearFiltersHandlerProps) => {
  setValue("name", "");
  setValue("patente", "");
  setValue("activo", undefined);
  setValue("tipoModulo", []);

  submitFilters?.(getValues());
  setFiltersApplied(false);
  setOpenFilters(false);
};

interface SubmitFiltersHandlerProps {
  setOpenFilters: Dispatch<SetStateAction<boolean>>;
  setFiltersApplied: Dispatch<SetStateAction<boolean>>;
  setCurrentFilters: Dispatch<SetStateAction<any>>;
  fetchTrucks: (filters?: any) => Promise<void>;
  event: FieldValues;
}

export const submitFiltersHandler = async ({
  event,
  setCurrentFilters,
  setFiltersApplied,
  fetchTrucks,
  setOpenFilters,
}: SubmitFiltersHandlerProps) => {
  setFiltersApplied(true);

  const filterObj: Partial<GetTrucksListProps> = getTrucksFilter(
    event as TLFRProps,
  );

  setCurrentFilters(filterObj);

  if (!Object.keys(filterObj)?.length) setFiltersApplied(false);

  await fetchTrucks(filterObj);

  setOpenFilters(false);
};

interface DeleteTrucksHandlerProps {
  selection: string[];
  setLoadingDeleteTrucks: Dispatch<SetStateAction<boolean>>;
  setOpenConfirm: Dispatch<SetStateAction<boolean>>;
  fetchTrucks: (filters?: any, showLoad?: boolean) => Promise<void>;
  setSelection: Dispatch<SetStateAction<string[]>>;
}

export const handleDeleteTrucksHandler = async ({
  fetchTrucks,
  selection,
  setLoadingDeleteTrucks,
  setOpenConfirm,
  setSelection,
}: DeleteTrucksHandlerProps) => {
  try {
    setLoadingDeleteTrucks(true);

    const idsToDelete = selection.map((id) => Number.parseInt(id));

    await deleteTrucks({ Ids: idsToDelete });

    toaster.create({
      title: "Camiones eliminados con éxito",
      type: "success",
    });

    setSelection([]);
    setOpenConfirm(false);
    fetchTrucks({}, false);
  } catch (error) {
    const errorMessage = getAxiosErrorMessage(
      error,
      "No se pudieron eliminar los camiones seleccionados.",
    );

    toaster.create({
      type: "error",
      title: errorMessage,
    });
  } finally {
    setLoadingDeleteTrucks(false);
  }
};

interface HandleCreateTruckHandlerProps {
  getValues: () => any;
  navigate: NavigateFunction;
  fetchPermissions: () => Promise<void>;
  setLoadingCreateTruck: (loading: boolean) => void;
}

export const handleCreateTruckHandler = async ({
  getValues,
  setLoadingCreateTruck,
  navigate,
  fetchPermissions,
}: HandleCreateTruckHandlerProps) => {
  try {
    setLoadingCreateTruck(true);

    const values = getValues();
    
    const objToSend: CreateTruckProps = {
      nombre: values.nombre.trim(),
      patente: values.patente.trim(),
      modulos: values.modulos,
    };

    await createTruck(objToSend);

    toaster.create({
      type: "success",
      title: "Camión creado con éxito.",
    });
    
    navigate(AppRoutes.TRUCKS);
  } catch (error) {
    const errorMessage = getAxiosErrorMessage(
      error,
      "No se pudo crear el camión",
    );
    
    const handled = await handlePermissionError(
      error,
      navigate,
      fetchPermissions,
      AppRoutes.PRODUCTS,
    );
    if (!handled) {
      toaster.create({
        type: "error",
        title: errorMessage,
      });
    }
    throw error;
  } finally {
    setLoadingCreateTruck(false);
  }
};

interface FetchModuleTypesHandlerProps {
  setLoadingModuleTypes: Dispatch<SetStateAction<boolean>>;
  setModuleTypes: Dispatch<SetStateAction<TipoModulo[] | null>>;
  fetchPermissions: () => Promise<void>;
  navigate: NavigateFunction;
}

export const fetchModuleTypesHandler = async ({
  setLoadingModuleTypes,
  setModuleTypes,
  fetchPermissions,
  navigate,
}: FetchModuleTypesHandlerProps) => {
  try {
    setLoadingModuleTypes(true);
    const response = await getTiposModulo();
    setModuleTypes(response);
    setLoadingModuleTypes(false);
  } catch (error) {
     const errorMessage = getAxiosErrorMessage(
      error,
      "No se pudieron obtener los tipos de pedido",
    );
    
    const handled = await handlePermissionError(
      error,
      navigate,
      fetchPermissions
    );
    if (!handled) {
      toaster.create({  
        type: "error",
        title: errorMessage,
      });
    }
    throw error;
  } finally {
    setLoadingModuleTypes(false);
  }
}

export interface FetchTruckHandlerProps {
  id: number
  setLoadingTruck: Dispatch<SetStateAction<boolean>>;
  getValues: () => any;
  navigate: NavigateFunction;
  setTruck: Dispatch<SetStateAction<TruckDetail | null>>;
  fetchPermissions: () => Promise<void>;
}

export const fetchTruckHandler = async ({ id, setLoadingTruck, navigate, setTruck, fetchPermissions }: FetchTruckHandlerProps) => {
    try {
      setLoadingTruck(true);
      const response = await getTruck({id});
      setTruck(response);
      setLoadingTruck(false);
    } catch (error) {
     const errorMessage = getAxiosErrorMessage(
      error,
      "No se pudo obtener el camión",
    );
    
    const handled = await handlePermissionError(
      error,
      navigate,
      fetchPermissions
    );
    if (!handled) {
      toaster.create({  
        type: "error",
        title: errorMessage,
      });
    }
    throw error;
  } finally {
    setLoadingTruck(false);
  }
}

export interface HandleUpdateTruckHandlerProps {
  id: number;
  values: FieldValues
  fetchPermissions: () => Promise<void>;
  getValues: () => any;
  navigate: NavigateFunction;
  setLoadingUpdateTruck: Dispatch<SetStateAction<boolean>>;
}

export const handleUpdateTruckHandler = async ({id, setLoadingUpdateTruck, navigate, fetchPermissions, values}: HandleUpdateTruckHandlerProps) => {
      try {
      setLoadingUpdateTruck(true);
      const objToSend: UpdateTruckProps = {
        id,
        nombre: values.nombre.trim(),
        patente: values.patente.trim(),
        modulos: values.modulos,
        activo: values.activo?.[0],
      }

      await updateTruck(objToSend);
     
 
      toaster.create({
        type: "success",
        title: "Camión actualizado con éxito",
      });
      setLoadingUpdateTruck(false);

      navigate(AppRoutes.TRUCKS);
    } catch (error) {
     const errorMessage = getAxiosErrorMessage(
      error,
      "No se pudo obtener el camión",
    );
    
    const handled = await handlePermissionError(
      error,
      navigate,
      fetchPermissions
    );
    if (!handled) {
      toaster.create({  
        type: "error",
        title: errorMessage,
      });
    }
    throw error;
  } finally {
    setLoadingUpdateTruck(false);
  }
}